import { MoreOutlined } from '@ant-design/icons'
import { Button, Dropdown, List, Menu } from 'antd'
import { ColumnFilterItem } from 'antd/lib/table/interface'
import React from 'react'
import { Row } from 'react-display-flex'

import { emptyCharacter } from '../../../../components/EmptyChar'
import { renderEmail } from '../../../../components/tables/table-column-renderers'
import { buildSearchFilterProps, sortStringColumn } from '../../../../components/tables/utils'
import { RfqEmail } from '../../models'

const renderEmails = (concatenatedEmails: string) => {
  const emails = concatenatedEmails
    .split(',')
    .map((email) => String(email).trim())
    .sort()

  return (
    <List
      bordered={false}
      size="small"
      dataSource={emails}
      renderItem={(item) => <List.Item>{renderEmail(item)}</List.Item>}
    />
  )
}

const Any = () => <Row className="faded-text">Any</Row>

const renderNameAndId = (name: string, id: number) => (name === emptyCharacter ? <Any /> : `${name} (${id})`)

const renderInstrumentCode = (instrumentCode: string) => (instrumentCode === emptyCharacter ? <Any /> : instrumentCode)

const renderActionsMenu =
  ({ onUpdate, onDelete }: { onUpdate: (rfqEmail: RfqEmail) => void; onDelete: (rfqEmail: RfqEmail) => void }) =>
  (rfqEmail: RfqEmail) => {
    const menu = (
      <Menu>
        <Menu.Item key="onUpdate" onClick={() => onUpdate(rfqEmail)}>
          Update RFQ Emails
        </Menu.Item>
        <Menu.Item key="onDelete" onClick={() => onDelete(rfqEmail)}>
          Delete RFQ Emails
        </Menu.Item>
      </Menu>
    )

    return (
      <Dropdown placement="bottomRight" overlay={menu}>
        <Button icon={<MoreOutlined />} />
      </Dropdown>
    )
  }

export const buildRfqEmailsTableColumns = ({
  onUpdate,
  onDelete,
  filters,
}: {
  onUpdate: (rfqEmail: RfqEmail) => void
  onDelete: (rfqEmail: RfqEmail) => void
  filters: {
    instrumentCodes: ColumnFilterItem[]
    displayNames: ColumnFilterItem[]
  }
}) => [
  {
    title: 'Issuer',
    dataIndex: 'issuerName',
    sorter: sortStringColumn({ dataIndex: 'issuerName' }),
    ...buildSearchFilterProps({
      dataIndex: 'issuerName',
      render: (issuerName: string, rfqEmail: RfqEmail) => renderNameAndId(issuerName, rfqEmail.issuerId),
    }),
  },
  {
    title: 'Tenant',
    dataIndex: 'tenantName',
    sorter: sortStringColumn({ dataIndex: 'tenantName' }),
    ...buildSearchFilterProps({
      dataIndex: 'tenantName',
      render: (tenantName: string, rfqEmail: RfqEmail) => renderNameAndId(tenantName, rfqEmail.tenantId),
    }),
  },
  {
    title: 'Instrument',
    dataIndex: 'instrumentCode',
    filters: filters.instrumentCodes,
    render: renderInstrumentCode,
    onFilter: (instrumentCode: string, rfqEmail: RfqEmail) => rfqEmail.instrumentCode === instrumentCode,
  },
  {
    title: 'Display Name',
    dataIndex: 'name',
    filters: filters.displayNames,
    onFilter: (displayName: string, rfqEmail: RfqEmail) => rfqEmail.name === displayName,
  },
  {
    title: 'Emails',
    dataIndex: 'email',
    ...buildSearchFilterProps({ dataIndex: 'email', render: renderEmails }),
  },
  { title: '', render: renderActionsMenu({ onUpdate, onDelete }) },
]
